import * as React from 'react';

import {FocusTrapZone} from '@fluentui/react/lib/FocusTrapZone';
import classnames from 'classnames';

import {Button} from '../button/button';
import {CloseButton} from '../button/close-button/close-button';
import {MinimizeButton} from '../button/minimize-button/minimize-button';
import {ChevronLeftIcon} from '../icon';

import styles from './modal-component.module.css';

type Props = {
    children?: any;
    className?: string;
    closeButton?: boolean;
    getBodyRef?: (arg1?: HTMLElement | null | undefined) => void;
    getCloseButtonRef?: (arg1?: HTMLButtonElement | null | undefined) => void;
    headerText?: string;
    headerSubtext?: string | React.ReactNode;
    headerIcon?: React.ReactNode;
    headerBackButton?: boolean;
    onHeaderBackButtonPress?: () => any;
    actionButton?: React.ReactNode;
    isClosing: boolean;
    isLeftAligned?: boolean;
    isWide?: boolean;
    noPadding?: boolean;
    noTopPadding?: boolean;
    onClose: () => void;
    onMinimize?: () => void;
    onOverlayClick: () => any;
    shouldTrapFocus?: boolean;
    maxHeight?: number;
    minHeight?: number;
    maxWidth?: number;
    minWidth?: number;
    fitHeight?: boolean;
    visibleOverflow?: boolean;
    isCentered?: boolean;
};

/*
 * Presentational component for our app modals. All styling
 * and actual UI rendering is done from this component.
 */
export class ModalComponent extends React.Component<Props> {
    render() {
        const styleName = this.props.isClosing
            ? styles['container--animate-out']
            : styles.container;
        const centerStyles = this.props.isCentered ? styles['container--centered'] : undefined;
        const agentMount = document.getElementById('team-ai-sidebar');
        const isAgentOpen = agentMount ? agentMount.getAttribute('data-is-open') === 'true' : false;

        return (
            <div data-is-ai-agent-open={isAgentOpen} className={styleName}>
                {this.props.shouldTrapFocus ? (
                    <FocusTrapZone
                        className={styles['focus-trap-zone']}
                        isClickableOutsideFocusTrap={true}
                    >
                        {this.props.isCentered ? (
                            <div className={centerStyles}>{this.renderModalContents()}</div>
                        ) : (
                            this.renderModalContents()
                        )}
                    </FocusTrapZone>
                ) : (
                    this.renderModalContents()
                )}
                <div onClick={this.props.onOverlayClick} className={styles.overlay} />
            </div>
        );
    }

    renderModalContents = () => {
        const bodyClassnames = classnames({
            [styles.body]: !this.props.isWide,
            [styles['body--wide']]: this.props.isWide,
            [styles['body--no-padding']]: this.props.noPadding,
            [styles['body--left-aligned']]: this.props.isLeftAligned,
            [styles['body--no-top-padding']]: this.props.noTopPadding,
            [styles['body--visible-overflow']]: this.props.visibleOverflow,
        });

        return (
            <div
                ref={this.props.getBodyRef}
                style={{
                    maxHeight: this.props.maxHeight ? this.props.maxHeight : undefined,
                    minHeight: this.props.minHeight ? this.props.minHeight : undefined,
                    maxWidth: this.props.maxWidth ? this.props.maxWidth : undefined,
                    minWidth: this.props.minWidth ? this.props.minWidth : undefined,
                    height: this.props.fitHeight ? 'fit-content' : undefined,
                }}
                className={classnames(this.props.className, bodyClassnames)}
            >
                {this.renderTopSection()}
                <div className={styles['main-section']}>{this.props.children}</div>
            </div>
        );
    };

    renderTopSection = () => {
        const {headerText, headerSubtext, headerIcon} = this.props;
        if (headerText) {
            return (
                <div className={styles['header-bar']}>
                    {this.renderHeaderBackButton()}
                    <div className={styles['header-text-container']}>
                        {headerIcon ? <div className='align-self-center'>{headerIcon}</div> : null}
                        <div className={styles['header-text']}>
                            <div>{headerText}</div>
                            {typeof headerSubtext === 'string' ? (
                                <div>{headerSubtext}</div>
                            ) : (
                                headerSubtext
                            )}
                        </div>
                    </div>
                    <div className='flex'>
                        {this.props.actionButton}
                        {this.renderCloseButtons()}
                    </div>
                </div>
            );
        }

        // If we don't have a header, but we do want a close button, we need to
        // absolutely position _just_ a close button on the right side.
        if (this.props.closeButton) {
            return this.renderCloseButtons();
        }

        return null;
    };

    renderCloseButtons = () => {
        return (
            <div className={styles['close-button-container']}>
                {this.props.onMinimize ? (
                    <MinimizeButton onClick={this.props.onMinimize} size={17} />
                ) : undefined}
                {this.props.closeButton ? (
                    <CloseButton
                        getRef={this.props.getCloseButtonRef}
                        tooltip='Close'
                        onClick={this.props.onClose}
                        size={17}
                    />
                ) : undefined}
            </div>
        );
    };

    renderHeaderBackButton = () => {
        if (this.props.headerBackButton && this.props.onHeaderBackButtonPress) {
            return (
                <Button
                    size='big'
                    icon={ChevronLeftIcon}
                    // @ts-expect-error
                    onClick={this.props.onHeaderBackButtonPress}
                >
                    Back
                </Button>
            );
        }

        return undefined;
    };
}
